/**
 * Recording Stream Builder
 * 
 * Responsible for creating MediaStreams for recording that combine:
 * - Video tracks (camera or screen share)
 * - Mixed audio tracks (microphone + AI audio)
 * 
 * Follows Single Responsibility Principle - only handles stream creation
 */

import { logger } from '@/lib/logging';

/**
 * Interface for AI audio service
 * Allows dependency inversion - we depend on abstraction, not concrete implementation
 */
export interface IAIAudioService {
  getAIAudioStream(): MediaStream | null;
}

export interface RecordingStreamConfig {
  videoSource: MediaStream;
  microphoneSource: MediaStream | null;
  aiAudioService: IAIAudioService | null;
  includeAIAudio?: boolean;
  mixingAudioContext?: AudioContext;
}

/**
 * Creates a MediaStream for recording with mixed audio
 * 
 * CRITICAL: MediaRecorder can only record ONE audio track, so we must mix
 * the microphone and AI audio into a single track using AudioContext.
 * 
 * @param config - Configuration for building the recording stream
 * @returns A MediaStream ready for recording
 */
export async function createRecordingStream(config: RecordingStreamConfig): Promise<MediaStream> {
  const {
    videoSource,
    microphoneSource,
    aiAudioService,
    includeAIAudio = true,
    mixingAudioContext: providedContext,
  } = config;

  const tracks: MediaStreamTrack[] = [];

  // Add video track
  const videoTrack = videoSource.getVideoTracks()[0];
  if (videoTrack) {
    tracks.push(videoTrack);
  } else {
    logger.warn('No video track found in video source');
  }

  // Mix audio tracks into a single track
  const mixedAudioTrack = await createMixedAudioTrack({
    microphoneSource,
    aiAudioService,
    includeAIAudio,
    mixingAudioContext: providedContext,
  });

  if (mixedAudioTrack) {
    tracks.push(mixedAudioTrack);
  }

  const mergedStream = new MediaStream(tracks);
  logger.info('Recording stream created', {
    videoTracks: mergedStream.getVideoTracks().length,
    audioTracks: mergedStream.getAudioTracks().length,
  });

  return mergedStream;
}

/**
 * Creates a mixed audio track from microphone and AI audio sources
 * 
 * NOTE: If mixingAudioContext is not provided, a new one is created.
 * The caller is responsible for managing the AudioContext lifecycle.
 * Consider reusing the same AudioContext across multiple calls to avoid resource leaks.
 */
async function createMixedAudioTrack(config: {
  microphoneSource: MediaStream | null;
  aiAudioService: IAIAudioService | null;
  includeAIAudio: boolean;
  mixingAudioContext?: AudioContext;
}): Promise<MediaStreamTrack | null> {
  const { microphoneSource, aiAudioService, includeAIAudio, mixingAudioContext: providedContext } = config;

  try {
    // Create or reuse mixing AudioContext
    const mixingContext = providedContext || new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Ensure AudioContext is running (await to prevent race conditions)
    if (mixingContext.state === 'suspended') {
      await mixingContext.resume().catch(err => {
        logger.warn('Failed to resume mixing AudioContext', { error: String(err) });
      });
    }

    // Create destination for mixed audio
    const mixedDestination = mixingContext.createMediaStreamDestination();

    let hasAudio = false;

    // Add microphone audio to mix
    const micAudio = microphoneSource?.getAudioTracks()[0];
    if (micAudio) {
      const micSource = mixingContext.createMediaStreamSource(new MediaStream([micAudio]));
      micSource.connect(mixedDestination);
      hasAudio = true;
      logger.info('Microphone audio added to mix', { trackId: micAudio.id });
    }

    // Add AI audio output to mix
    if (includeAIAudio && aiAudioService) {
      const aiAudioStream = aiAudioService.getAIAudioStream();
      const aiAudioTrack = aiAudioStream?.getAudioTracks()[0];
      if (aiAudioTrack) {
        const aiSource = mixingContext.createMediaStreamSource(new MediaStream([aiAudioTrack]));
        aiSource.connect(mixedDestination);
        hasAudio = true;
        logger.info('AI audio added to mix', { trackId: aiAudioTrack.id });
      } else {
        logger.warn('AI audio stream not available for recording');
      }
    }

    // Get the mixed audio track
    if (hasAudio) {
      const mixedAudioTrack = mixedDestination.stream.getAudioTracks()[0];
      if (mixedAudioTrack) {
        logger.info('Mixed audio track created for recording');
        return mixedAudioTrack;
      }
    } else {
      logger.warn('No audio tracks available for recording');
    }
  } catch (err) {
    logger.error('Failed to mix audio tracks', { error: String(err) });
  }

  // Fallback: return microphone audio directly if mixing fails
  const micAudio = microphoneSource?.getAudioTracks()[0];
  if (micAudio) {
    logger.warn('Using microphone audio only (mixing failed)');
    return micAudio;
  }

  return null;
}

